import * as React from "react";
import { Form, Label } from "semantic-ui-react";
import { FormBaseComponent, FormState } from "@neworbit/simpleui-forms";
import { Input } from "@neworbit/simpleui-input";

import { createValidator } from "not-valid";

import * as moment from "moment";

import { ClinicErrors, MobileClinicCreateModel } from "../models";
import { ClinicianSelector } from "../../components/ClinicianSelector";
import { PartialCustomerListModel } from "../../../crud/customer";

interface Props {
    createClinicErrors: ClinicErrors;
    customers: PartialCustomerListModel[];
    loadCustomers: () => void;
}

type State = FormState<MobileClinicCreateModel> & { showConflictErrors?: boolean };

export class CreateMobileClinicForm
    extends FormBaseComponent<MobileClinicCreateModel, Props, State> {

    public state: State = {
        values: {
            mobileUnit: "",
            address: "",
            start: null,
            end: null,
            cliniciansRequired: 1,
            teamPreventUsers: [],
            customerId: null
        },
        valid: {}
    };

    private notInPastValidator = createValidator((value: moment.Moment) => value && value.isSameOrAfter(moment()), "Must not be in the past");

    public componentDidMount() {
        if (this.props.customers.length === 0) {
            this.props.loadCustomers();
        }
    }

    public render() {
        const { start, end } = this.state.values;
        const calendarHighlight = start && end && start.isValid() && end.isValid() && { start, end };
        return (
            <Form onSubmit={this.handleSubmit} className="clinic-create-form">
                <Input.Text
                    label="Mobile Unit"
                    value={this.state.values.mobileUnit}
                    showErrors={this.state.showErrors}
                    required
                    onChange={this.onChangeMobileUnit}
                />
                <Input.Text
                    label="Address"
                    value={this.state.values.address}
                    showErrors={this.state.showErrors}
                    required
                    onChange={this.onChangeAddress}
                />
                <Input.Datetime
                    label="Start"
                    value={this.state.values.start}
                    showErrors={this.state.showErrors}
                    validation={[this.notInPastValidator]}
                    required
                    onChange={this.onChangeStart}
                />
                <Input.Datetime
                    label="End"
                    value={this.state.values.end}
                    showErrors={this.state.showErrors}
                    validation={[this.notInPastValidator]}
                    required
                    onChange={this.onChangeEnd}
                />
                {this.state.showErrors && !this.validateStartBeforeEndDate() &&
                    <Label basic color="red" pointing content="The start time must be before the end" />}
                {this.state.showErrors && !this.validateStartEndDateSameDay() &&
                    <Label basic color="red" pointing content="The start time must be the same day as the end" />}
                <Input.Number
                    label="Clinicians Required"
                    value={this.state.values.cliniciansRequired}
                    showErrors={this.state.showErrors}
                    required
                    onChange={this.onChangeCliniciansRequired}
                />
                <Input.Dropdown
                    label="Customer"
                    value={this.state.values.customerId}
                    showErrors={this.state.showErrors}
                    required
                    onChange={this.onChangeCustomer}
                    options={this.props.customers.map(c => ({ value: c.id, text: c.name }))}
                    dynamicOptions
                />
                <ClinicianSelector
                    restrictToOneClinician
                    selectedClinicians={this.state.values.teamPreventUsers}
                    highlight={calendarHighlight}
                    conflicts={this.state.showConflictErrors && this.props.createClinicErrors && this.props.createClinicErrors.errors.userClashIds}
                    onCliniciansChanged={this.onClinicansChanged}
                />
            </Form>
        );
    }

    public submit = () => {
        this.setState({ showConflictErrors: true });
        this.handleSubmit({ preventDefault: (): void => undefined } as any);
    };

    private validateStartBeforeEndDate = () => {
        return !this.state.values.start || !this.state.values.end || this.state.values.end.diff(this.state.values.start) > 0;
    };

    private validateStartEndDateSameDay = () => {
        return !this.state.values.start || !this.state.values.end || this.state.values.end.isSame(this.state.values.start, "day");
    };

    private onChangeMobileUnit = (value: string, valid: boolean) => this.updateProperty("mobileUnit", value, valid);

    private onChangeAddress = (value: string, valid: boolean) => this.updateProperty("address", value, valid);

    private onChangeStart = (value: moment.Moment, valid: boolean) => {
        const datesValid = this.validateStartBeforeEndDate() && this.validateStartEndDateSameDay();
        this.updateProperty("start", value && value.utc(true), valid && datesValid);
        this.updateProperty("end", this.state.values.end, this.notInPastValidator(this.state.values.end).type === "pass" && datesValid);
        this.setState({ showConflictErrors: false });
    };

    private onChangeEnd = (value: moment.Moment, valid: boolean) => {
        const datesValid = this.validateStartBeforeEndDate() && this.validateStartEndDateSameDay();
        this.updateProperty("end", value && value.utc(true), valid);
        this.updateProperty("start", this.state.values.start, this.notInPastValidator(this.state.values.start).type === "pass" && datesValid);
        this.setState({ showConflictErrors: false });
    };

    private onChangeCliniciansRequired = (value: number, valid: true) => this.updateProperty("cliniciansRequired", value, valid);

    private onClinicansChanged = (clinicians: { id: string; name: string }[]) => {
        this.updateProperty("teamPreventUsers", clinicians, clinicians.length <= 1);
        this.setState({ showConflictErrors: false });
    };

    private onChangeCustomer = (customerId: string, valid: true) => this.updateProperty("customerId", customerId, valid);
}
